import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  type QueryKey,
  useQueryClient,
} from '@tanstack/react-query';

import { logger } from '@client/utils/logger.util';

interface StoreMutationOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
> extends UseMutationOptions<TData, TError, TVariables, TContext> {
  invalidateQueries?: QueryKey[];
}

/**
 * Custom hook for performing mutations in the store.
 * @param {StoreMutationOptions} options - The options for the mutation.
 * @returns {UseMutationResult} - The result of the mutation.
 * @template TData - The type of the data returned by the mutation.
 */
export function useStoreMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: StoreMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      try {
        // Run original onSuccess first
        await options.onSuccess?.(data, variables, context, mutation);

        // Handle query invalidation
        if (options.invalidateQueries) {
          await Promise.all(
            options.invalidateQueries.map((queryKey) =>
              queryClient.invalidateQueries({ queryKey }),
            ),
          );
        }
      } catch (error) {
        logger.error('Error in mutation side effects:', error);
      }
    },
  });
}
